import { Card } from '@app/components/various/Card';
import { RequestStatus } from '@app/components/various/RequestStatus';
import { compiledPaths } from '@app/paths';
import { useAccountRequestListOptions } from '@app/utils/hooks/accounts/useAccountRequestListOptions';
import { useTableColumnsConfiguration } from '@app/utils/hooks/table/useTableColumnsConfiguration';
import { useAccountRequestStatusLabel } from '@app/utils/hooks/useAccountRequestStatusCell';
import { isEmpty } from '@app/utils/isEmpty';
import { getAccountRequestsStatusColor } from '@app/utils/status';
import { Row } from '@tanstack/react-table';
import { Lookbook } from '@typings';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Props {
  row: Row<Lookbook.AccountRequestListing>;
  openCreateModal: VoidFunction;
  openDeclineModal: VoidFunction;
  openArchiveModal: VoidFunction;
  onSetToPending: VoidFunction;
}

export const AccountRequestsListCard = ({ row, openCreateModal, openDeclineModal, openArchiveModal, onSetToPending }: Props) => {
  const { company, email, status, createdAt, source, id, firstName, lastName } = row.original;

  const { t } = useTranslation(['accounts', 'common', 'requests']);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'accountRequests' });
  const { getStatusLabel } = useAccountRequestStatusLabel();

  const menuItems = useAccountRequestListOptions({
    accountRequestStatus: status,
    onSetToPending,
    openArchiveModal,
    openCreateModal,
    openDeclineModal,
  });

  return (
    <Card.Wrapper markerColor={getAccountRequestsStatusColor(status)}>
      {columnsVisibility.status?.isVisible && (
        <Card.Header>
          <RequestStatus type={status} />
          <span>
            {t('common:status')}: <span className="text-neutral-20">{getStatusLabel(status)}</span>
          </span>
        </Card.Header>
      )}
      <Card.Title>
        <Link
          data-sentry-mask
          to={compiledPaths.ACCOUNT_REQUEST_DETAILS({ accountRequestId: id })}
          className="absolute-cursor-pointer focus-visible:focus-outline"
        >
          {company}
        </Link>
      </Card.Title>
      <Card.AttributeList className="flex-1">
        {columnsVisibility.email?.isVisible && <Card.Attribute value={email} name={t('accounts:contact')} />}
        {columnsVisibility.fullName?.isVisible && <Card.Attribute value={`${firstName} ${lastName}`} name={t('common:name')} />}
        {columnsVisibility.source_name?.isVisible && (
          <Card.Attribute
            value={<Link to={compiledPaths.LOOKBOOK_SINGLE({ lookbookId: source?.data.lookbookHash })}>{source?.name}</Link>}
            name={t('accounts:source')}
          />
        )}
        {columnsVisibility.createdAt?.isVisible && <Card.Attribute value={createdAt} name={t('requests:requested')} />}
      </Card.AttributeList>
      {!isEmpty(menuItems) && (
        <Card.Menu title={`${company} ${t('accounts:request')}`} items={menuItems} className="z-interactiveTableItem" />
      )}
    </Card.Wrapper>
  );
};
